import { useParams, Navigate } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import JobApplicationForm from "@/components/feature/JobApplicationForm";
import { jobsData } from "@/mocks/careersData";

function DetailList({ title, items, icon }: { title: string; items: string[]; icon: string }) {
  return (
    <AnimatedSection>
      <div className="rounded-2xl bg-background-50 border border-background-200/60 p-6 md:p-7 h-full">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
            <i className={`${icon} text-lg`} />
          </span>
          <h3 className="text-base md:text-lg font-bold text-foreground-950 font-heading">{title}</h3>
        </div>
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-600 leading-relaxed">
              <span className="w-5 h-5 mt-0.5 shrink-0 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-600">
                <i className="ri-check-line text-xs" />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </AnimatedSection>
  );
}

export default function JobDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const job = jobsData.find((j) => j.slug === slug);

  if (!job) {
    return <Navigate to="/careers" replace />;
  }

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Careers", href: "/careers" },
          { label: job.title },
        ]}
      />

      {/* Job Hero */}
      <section className="relative py-16 md:py-20 bg-hero-gradient overflow-hidden">
        <div className="container-astro relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <AnimatedSection>
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-xl text-white shrink-0"
                  style={{ backgroundColor: job.color }}
                >
                  <i className={`${job.icon} text-2xl`} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent-300 mb-1">
                    Open Position
                  </p>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-background-50 font-heading tracking-tight">
                    {job.title}
                  </h1>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-background-50/10 border border-background-500/30 text-background-100 text-xs font-semibold">
                  <i className="ri-briefcase-line" />
                  {job.experience}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-background-50/10 border border-background-500/30 text-background-100 text-xs font-semibold">
                  <i className="ri-map-pin-line" />
                  {job.location}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-accent-500/20 border border-accent-400/30 text-accent-200 text-xs font-semibold">
                  <i className="ri-time-line" />
                  {job.type}
                </span>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={150}>
              <a
                href="#apply"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap"
              >
                Apply Now
                <i className="ri-arrow-right-line text-xs" />
              </a>
            </AnimatedSection>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
      </section>

      {/* Job Body */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {/* Details */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <AnimatedSection>
                <div className="rounded-2xl bg-background-100 border border-background-200/60 p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading">
                    About the Role
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{job.description}</p>
                </div>
              </AnimatedSection>

              <DetailList
                title="Responsibilities"
                icon="ri-task-line"
                items={job.responsibilities}
              />
              <DetailList
                title="Requirements"
                icon="ri-user-settings-line"
                items={job.requirements}
              />
              <DetailList
                title="Good to Have"
                icon="ri-star-line"
                items={job.niceToHave}
              />
              <DetailList
                title="What We Offer"
                icon="ri-gift-line"
                items={job.benefits}
              />
            </div>

            {/* Apply form */}
            <div className="lg:col-span-1">
              <div
                id="apply"
                className="sticky top-24 rounded-2xl bg-background-100 border border-background-200/60 p-6 md:p-7"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                    <i className="ri-send-plane-line text-lg" />
                  </span>
                  <h3 className="text-lg font-bold text-foreground-950 font-heading">
                    Apply for this role
                  </h3>
                </div>
                <p className="text-xs text-foreground-500 mb-6">
                  Fields marked <span className="text-rose-500">*</span> are required.
                </p>
                <JobApplicationForm role={job.title} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}